import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ParticleField } from "@/components/ui/ParticleField";
import { Book3D } from "@/components/ui/Book3D";
import { EmailCapture } from "@/components/ui/EmailCapture";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { AnimatedCard } from "@/components/ui/AnimatedCard";
import { SmoothImage } from "@/components/ui/SmoothImage";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { cn } from "@/lib/utils";
import { formatEventDate, isInProgress, splitEvents } from "@/data/events";
import { ArrowRight, Flame, Star, AudioLines, ExternalLink, ChevronDown, Sparkles, Brain } from "lucide-react";
import heroBgMobile from "@/assets/hero/hero-cosmic-bg-mobile.webp";
import heroBgDesktop from "@/assets/hero/hero-cosmic-bg.webp";
import bookCover from "@/assets/cover-book.webp";
import michelePhoto from "@/assets/michele-photo.webp";
import publisherLogo from "@/assets/sacred-dragon-logo.webp";

const personJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Michele London",
  "url": "https://www.michelelondonchanneler.com",
  "jobTitle": "Channeled Author, Spiritual Teacher, Reiki Master",
  "description": "Michele London is a channeled author, spiritual teacher, and certified Reiki Master. Author of Songs of the Mantis.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "11 Birchwood Park Dr",
    "addressLocality": "Syosset",
    "addressRegion": "NY",
    "postalCode": "11791",
    "addressCountry": "US"
  },
  "sameAs": [
    "https://www.facebook.com/wheatinchaff",
    "https://www.instagram.com/wheatinchaff",
    "https://www.linkedin.com/in/michelelondon",
    "https://www.tiktok.com/@wheatinchaff",
    "https://www.youtube.com/@wheatinchaffhealinghearts"
  ],
  "worksFor": {
    "@type": "Organization",
    "name": "Wheat In Chaff Healing Hearts"
  }
};

export default function HomePage() {
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  // Prefer a dated, in-person event that hasn't started. Plain `upcoming[0]` would pin the
  // strip to the 09.10 Reiki course until 10.22, hiding the 09.24 / 09.26 / 09.29 / 10.02 talks.
  const { upcoming } = splitEvents();
  const nextEvent =
    upcoming.find((e) => !isInProgress(e) && !e.online && e.type !== "class") ??
    upcoming.find((e) => !isInProgress(e)) ??
    upcoming[0];

  return <Layout>
      <SEO
        title="Michele London | Channeled Author, Spiritual Teacher, Reiki Master"
        description="Michele London is a channeled author, spiritual teacher, and Reiki Master. Explore her book Songs of the Mantis, speaking topics, healing services, and more."
        canonical="/"
      >
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
      </SEO>

      {/* Welcome/Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Cosmic background — ONE image per device.
            Previously two <img> hidden with `hidden md:block` / `md:hidden`, which still
            downloaded BOTH files (mobile paid 147 KB for a desktop image it never showed).
            <picture> lets the browser pick one. Imported (not /public) so Vite emits a
            content-hashed filename the CDN can cache immutably; index.html preloads the
            same hashed URLs — this is the LCP element. */}
        <picture>
          <source media="(min-width: 768px)" srcSet={heroBgDesktop} width={1920} height={1080} />
          <img
            src={heroBgMobile}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            width={768}
            height={1024}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/80" />

        {/* Particles - animated stars/cosmic effect */}
        <ParticleField particleCount={50} />

        {/* Content */}
        <div className="relative z-10 section-container py-16 md:py-20">
          {/* Desktop Layout - 2 columns */}
          <div className="hidden md:grid md:grid-cols-5 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Column - Photo */}
            <div className={cn(
              "md:col-span-2 flex flex-col items-center transition-all duration-700 ease-out",
              heroImageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}>
              <h1 className="font-serif text-2xl md:text-3xl text-white text-center mb-6" style={{
                textShadow: '0 0 8px rgba(255,255,255,0.3), 0 0 16px rgba(255,255,255,0.15), 0 2px 12px rgba(0,0,0,0.8)'
              }}>
                Welcome!
              </h1>
              
              <div className="relative">
                <SmoothImage
                  src={michelePhoto}
                  alt="Michele London, channeled author and spiritual teacher"
                  width={448}
                  height={448}
                  className="w-80 lg:w-96 rounded-lg shadow-elegant"
                  placeholderClassName="w-80 lg:w-96 h-96 lg:h-[480px]"
                  onLoad={() => setHeroImageLoaded(true)}
                />
                <div className="absolute -inset-1 bg-gradient-to-r from-gold/20 via-secondary/20 to-gold/20 rounded-lg blur-sm -z-10" />
              </div>
              
              <p className="text-3xl md:text-4xl text-white mt-6" style={{
                fontFamily: "'P22 Cezanne Pro', cursive",
                textShadow: '0 0 8px rgba(255,255,255,0.3), 0 0 16px rgba(255,255,255,0.15)'
              }}>
                Michele
              </p>
            </div>
            
            {/* Right Column - Text */}
            <div className={cn(
              "md:col-span-3 space-y-4 text-white font-sans text-base lg:text-lg leading-relaxed text-left transition-all duration-700 ease-out delay-150",
              heroImageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )} style={{
              textShadow: '0 2px 12px rgba(0,0,0,0.8)'
            }}>
              <p>
                As a healer, spiritual teacher, Trance Channel, and now author of <em className="text-gold">Songs of the Mantis: Light Codes for Earth's Ascension</em>, I extend an invitation to explore, discern, and experience a deeper understanding for your spiritual journey.
              </p>
              <p>
                Here, you'll discover a living body of work that weaves within it, cosmic insights amidst grounded human guidance. These teachings illuminate the path of awakening using transparency, compassion, and practical tools for your everyday spiritual life.
              </p>
              <p>
                You stand at the threshold of a planetary turning. <em className="text-gold">Songs of the Mantis</em> is not simply a book — it is a signal, a harmonic, a remembrance for those who came here to awaken in this lifetime.
              </p>
              <p>
                This space welcomes the ones who feel Earth stirring, the ones who have sensed change moving beneath their feet long before they had language for it.
              </p>
              <p>
                Explore the messages Channeled from Chow, a high-vibrational Mantis being and guide. Learn about my lectures, events, and upcoming offerings. And step into a community of star-seeds, empaths, and seekers who know they're here for something more.
              </p>
              <p className="font-medium">
                This isn't just a website. It's an invitation...for YOU!
              </p>
            </div>
          </div>
          
          {/* Mobile Layout - Stacked */}
          <div className={cn(
            "md:hidden max-w-lg mx-auto transition-all duration-700 ease-out",
            heroImageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}>
            <h1 className="font-serif text-2xl sm:text-3xl text-white text-center" style={{
              textShadow: '0 0 8px rgba(255,255,255,0.3), 0 0 16px rgba(255,255,255,0.15), 0 2px 12px rgba(0,0,0,0.8)'
            }}>
              Welcome!
            </h1>
            
            <div className="flex justify-center mt-6">
              <div className="relative">
                <SmoothImage
                  src={michelePhoto}
                  alt="Michele London, channeled author and spiritual teacher"
                  width={448}
                  height={448}
                  className="w-64 sm:w-72 rounded-lg shadow-elegant"
                  placeholderClassName="w-64 sm:w-72 h-72 sm:h-80"
                  onLoad={() => setHeroImageLoaded(true)}
                />
                <div className="absolute -inset-1 bg-gradient-to-r from-gold/20 via-secondary/20 to-gold/20 rounded-lg blur-sm -z-10" />
              </div>
            </div>
            
            <p className="text-3xl text-white mt-4 text-center" style={{
              fontFamily: "'P22 Cezanne Pro', cursive",
              textShadow: '0 0 8px rgba(255,255,255,0.3), 0 0 16px rgba(255,255,255,0.15)'
            }}>
              Michele
            </p>
            
            <div className="mt-6 space-y-3 text-white font-sans text-sm leading-relaxed text-left" style={{
              textShadow: '0 2px 12px rgba(0,0,0,0.8)'
            }}>
              <p>
                As a healer, spiritual teacher, Trance Channel, and now author of <em className="text-gold">Songs of the Mantis: Light Codes for Earth's Ascension</em>, I extend an invitation to explore, discern, and experience a deeper understanding for your spiritual journey.
              </p>
              <p>
                Here, you'll discover a living body of work that weaves within it, cosmic insights amidst grounded human guidance. These teachings illuminate the path of awakening using transparency, compassion, and practical tools for your everyday spiritual life.
              </p>
              <p>
                You stand at the threshold of a planetary turning. <em className="text-gold">Songs of the Mantis</em> is not simply a book — it is a signal, a harmonic, a remembrance for those who came here to awaken in this lifetime.
              </p>
              <p>
                This space welcomes the ones who feel Earth stirring, the ones who have sensed change moving beneath their feet long before they had language for it.
              </p>
              <p>
                Explore the messages Channeled from Chow, a high-vibrational Mantis being and guide. Learn about my lectures, events, and upcoming offerings. And step into a community of star-seeds, empaths, and seekers who know they're here for something more.
              </p>
              <p className="font-medium">
                This isn't just a website. It's an invitation...for YOU!
              </p>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-8 md:mt-12">
            <button onClick={() => document.getElementById('book-showcase')?.scrollIntoView({
            behavior: 'smooth'
          })} className="animate-fade-in animation-delay-700 cursor-pointer group" aria-label="Scroll to next section">
              <div className="flex flex-col items-center text-white group-hover:scale-110 transition-transform">
                <ChevronDown className="w-8 h-8 md:w-10 md:h-10 animate-bounce" strokeWidth={2.5} />
                <ChevronDown className="w-8 h-8 md:w-10 md:h-10 -mt-5 md:-mt-6 animate-bounce opacity-60" strokeWidth={2.5} />
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Next Event Strip - hidden when nothing is upcoming */}
      {nextEvent && (
        <section className="py-8 bg-background border-b border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-display text-xs tracking-wider text-gold">NEXT EVENT</p>
                <h2 className="mt-1 font-serif text-xl sm:text-2xl text-foreground">
                  {nextEvent.title}
                </h2>
                <p className="mt-1 text-muted-foreground text-sm">
                  {formatEventDate(nextEvent)}
                  {" "}<span aria-hidden="true">·</span>{" "}
                  {nextEvent.venue}
                  {" "}<span aria-hidden="true">·</span>{" "}
                  {nextEvent.online ? "Online" : nextEvent.city}
                </p>
              </div>
              <Button asChild variant="outline-gold" size="sm" className="flex-shrink-0 w-full sm:w-auto">
                <Link to="/events">
                  All events
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Book Showcase Section */}
      <section id="book-showcase" className="py-16 bg-card relative overflow-hidden scroll-mt-20">
      
        {/* Decorative glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        
        <div className="section-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Book Cover */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative group">
                {/* Persistent bright glow, stronger on hover */}
                <div className="absolute -inset-4 bg-gold/35 rounded-2xl blur-2xl transition-all duration-500 group-hover:bg-gold/55 group-hover:blur-3xl" />
                <div className="absolute -inset-2 bg-gold/15 rounded-xl blur-md" />
                <img src={bookCover} alt="Songs of the Mantis book cover by Michele London" decoding="async" width={320} height={495} className="relative w-64 md:w-80 rounded-lg shadow-elegant transition-transform duration-500 group-hover:scale-105" />
              </div>
            </div>
            
            {/* Book Info */}
            <div className="text-center lg:text-left">
              <p className="font-display text-xs sm:text-sm tracking-wider text-gold mb-4 break-words">
                RELEASED MAY 2026
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-page-title text-foreground break-words">
                <em>Songs of the Mantis</em>
              </h2>
               <p className="font-serif text-lg md:text-xl mt-2" style={{ color: '#FFFFFF' }}>
                 Light Codes for Earth's Ascension
               </p>
              <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                <em>Songs of the Mantis</em> gathers moments of experiential healing and insight - inviting readers to quietly remember who they are. It is a living narrative meant to be felt, not merely read.
              </p>
              {/* Space for future endorsement quote */}
              <div className="mt-6 min-h-[2rem]">
                {/* Endorsement quote placeholder - to be added in February */}
              </div>
              <div className="mt-8">
                <Button asChild variant="gold" size="lg" className="w-full sm:w-auto">
                  <Link to="/book">
                    Learn More About the Book
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Teaser Section */}
      <section className="py-16 bg-background">
        <div className="section-container">
          <SectionHeading subtitle="Explore Michele's healing practice and transformative services">
            Beyond The Book
          </SectionHeading>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 sm:mt-12 max-w-md md:max-w-none mx-auto auto-rows-fr">
            <a href="https://www.wheatinchaff.com/service-page/60-minute-intuitive-reiki-session" target="_blank" rel="noopener noreferrer" className="flex">
              <AnimatedCard delay={0} className="py-4 group cursor-pointer flex-1 flex flex-col">
                <div className="text-center flex flex-col flex-1">
                  <div className="w-14 h-14 mx-auto rounded-full bg-secondary/20 flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-secondary/30">
                    <Flame className="w-7 h-7 text-secondary fill-secondary/20 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="font-serif text-xl text-foreground mb-2">Reiki Healing</h3>
                  <p className="text-muted-foreground text-sm">Energy healing sessions incorporating crystals and sound, customized uniquely for each person</p>
                </div>
              </AnimatedCard>
            </a>

            <a href="https://www.wheatinchaff.com/service-page/light-language-certification-virtual" target="_blank" rel="noopener noreferrer" className="flex">
              <AnimatedCard delay={100} className="py-4 group cursor-pointer flex-1 flex flex-col">
                <div className="text-center flex flex-col flex-1">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gold/20 flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-gold/30">
                    <Star className="w-7 h-7 text-gold fill-gold/20 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="font-serif text-xl text-foreground mb-2">Light Language</h3>
                  <p className="text-muted-foreground text-sm">Ancient multidimensional communication for healing and spiritual transformation</p>
                </div>
              </AnimatedCard>
            </a>

            <a href="https://www.wheatinchaff.com/service-page/sound-bath-on-location-1" target="_blank" rel="noopener noreferrer" className="flex">
              <AnimatedCard delay={200} className="py-4 group cursor-pointer flex-1 flex flex-col">
                <div className="text-center flex flex-col flex-1">
                  <div className="w-14 h-14 mx-auto rounded-full bg-lavender/20 flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-lavender/30">
                    <AudioLines className="w-7 h-7 text-lavender fill-lavender/20 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="font-serif text-xl text-foreground mb-2">Sound Healing</h3>
                  <p className="text-muted-foreground text-sm">Shamanic sound healing and sacred frequencies for deep spiritual experiences</p>
                </div>
              </AnimatedCard>
            </a>

            <a href="https://www.wheatinchaff.com/personalized-meditations" target="_blank" rel="noopener noreferrer" className="flex">
              <AnimatedCard delay={300} className="py-4 group cursor-pointer flex-1 flex flex-col">
                <div className="text-center flex flex-col flex-1">
                  <div className="w-14 h-14 mx-auto rounded-full bg-teal/20 flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-teal/30">
                    <Brain className="w-7 h-7 text-teal fill-teal/20 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="font-serif text-xl text-foreground mb-2">Personalized Meditations</h3>
                  <p className="text-muted-foreground text-sm">Your journey, your meditation, your peace; crafted for your soul</p>
                </div>
              </AnimatedCard>
            </a>
          </div>

          <div className="mt-12 text-center">
            <Button asChild variant="outline-mantis" size="lg" className="w-full sm:w-auto">
              <Link to="/services">
                Explore Full Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Publisher & Email Capture Section */}
      <section className="py-16 bg-gradient-cosmic relative overflow-hidden">
        <ParticleField particleCount={20} />
        
        <div className="section-container relative z-10">
          <div className="text-center mb-12">
            <p className="text-sm text-muted-foreground mb-4">Published by</p>
            <div className="flex flex-col items-center gap-4">
              <img
                src={publisherLogo}
                alt="Sacred Dragon Publishing logo"
                className="w-16 h-16 md:w-20 md:h-20 object-contain transition-transform duration-300 hover:scale-110"
              />
              <h3 className="font-display text-2xl md:text-3xl text-gold tracking-wide">
                Sacred Dragon Publishing
              </h3>
            </div>
          </div>
          
          <div className="max-w-xl mx-auto glass rounded-2xl p-6 sm:p-8 md:p-12">
            <h3 className="font-serif text-2xl text-foreground text-center mb-4">
              Stay Connected
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Receive updates, special promotions, and exclusive content
            </p>
            <EmailCapture showDescription={false} />
          </div>
        </div>
      </section>
    </Layout>;
}
